import type { CSSProperties } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  motion,
  useReducedMotion,
  useSpring,
  useTransform,
} from "framer-motion";

/** Most decimal places we will ever display, guarding float artefacts. */
const MAX_DECIMALS = 4;

/**
 * Decimal places a number carries in its own literal form: 74.6 -> 1, 42 -> 0.
 *
 * Whole numbers report 0 whatever their literal form. Reading exponential
 * notation off the string alone would count a mantissa's digits ("1.2e+21"
 * -> 2), and scaling by that factor costs precision at the top of the range:
 * 1.7e22 came back 2097152 heavier.
 */
export function decimalPlaces(value: number): number {
  if (Number.isInteger(value)) return 0;
  const str = String(value);
  const dot = str.indexOf(".");
  if (dot === -1) return 0;
  return Math.min(str.length - dot - 1, MAX_DECIMALS);
}

/**
 * Fraction digits an authored literal carries: "45.60" -> 2, "1,234.5" -> 1.
 *
 * `decimalPlaces` cannot answer this, because by the time a written value is a
 * number its trailing zeros are gone: `parseFloat("45.60")` is `45.6`, which
 * reports one place and renders `$45.6` for a price the author wrote in cents.
 * Callers holding the source text pass this through so the count-up shows what
 * was written — the same string the non-animated branch renders verbatim.
 */
export function authoredPlaces(literal: string): number {
  const dot = literal.indexOf(".");
  if (dot === -1) return 0;
  return Math.min(literal.length - dot - 1, MAX_DECIMALS);
}

/**
 * Fraction digits a frame of the count-up is shown at.
 *
 * The target's own precision, so an authored decimal survives to the final
 * frame — except that a target between 0 and 10 is smoothed to at least 1dp
 * *while climbing*, or a count to 7 would step through four visible integers.
 *
 * The single source of that rule: the quantiser rounds frames with it, and the
 * component sizes its width reservation with it (`animating: true` gives the
 * widest frame the climb can produce). Two copies of the rule would drift, and
 * a reservation narrower than a frame is the re-flow this all exists to stop.
 *
 * The smoothing needs a target above zero to be worth anything, and asking for
 * it is how the reservation over-reserves: a spring at rest on 0 (or descending
 * to a negative target) never satisfies the quantiser's `latest < target`, so
 * no frame it can produce carries that extra digit. Without the guard a
 * `value={0}` reserves "0.0" and renders "0" for ever.
 */
export function countUpPlaces(
  target: number,
  animating: boolean,
  authored?: number,
): number {
  const decimals = authored ?? decimalPlaces(target);
  return animating && target > 0 && target < 10
    ? Math.max(decimals, 1)
    : decimals;
}

/**
 * Quantise an in-flight spring value for display.
 *
 * Rounds to the *target's* own precision so an authored decimal survives to
 * the final frame — previously every value was passed through `Math.round`,
 * which silently rendered a written `74.6` as `75`.
 *
 * Whole-number targets are unaffected (`decimalPlaces` is 0, so this collapses
 * to `Math.round`), and small whole numbers keep the 1dp smoothing they used
 * while counting up.
 *
 * `authored` rounds frames at the written precision instead of the number's
 * own, so a `$45.60` counts through real hundredths ("$12.37") rather than
 * dragging a frozen zero up the screen.
 */
export function quantizeCountUp(
  latest: number,
  target: number,
  authored?: number,
): number {
  const factor = 10 ** countUpPlaces(target, latest < target, authored);
  return Math.round(latest * factor) / factor;
}

/**
 * Apply the caller's formatter, falling back to locale digit grouping.
 *
 * The fallback asks for `MAX_DECIMALS` fraction digits: `toLocaleString`
 * otherwise caps plain numbers at 3, which would re-round an authored
 * `1.2345` back to `1.235` after `quantizeCountUp` preserved it.
 *
 * `minFractionDigits` pins the low end for the same reason the count-up
 * quantises to the target's precision: locale formatting drops a trailing
 * zero, so an in-flight `32.0` renders as `32` — a character narrower than
 * the `32.3` it settles on, which shifts every digit when the point comes
 * back. Passing the target's own decimal count holds the fraction width for
 * the whole climb.
 *
 * It applies to the fallback only. A `format` callback is used verbatim, so
 * such a caller owns its own fraction digits: the reservation still pins the
 * element's outer width (it is built from `format`'s own output), but a
 * formatter that drops a trailing zero will still shuffle its digits inside
 * that box. `formatCountUpValue` deliberately does not second-guess it —
 * re-formatting a caller's string is how the authored `74.6` got rounded to
 * `75` in the first place.
 */
export function formatCountUpValue(
  value: number,
  format?: (value: number) => string,
  minFractionDigits = 0,
): string {
  return format
    ? format(value)
    : value.toLocaleString(undefined, {
        minimumFractionDigits: minFractionDigits,
        maximumFractionDigits: MAX_DECIMALS,
      });
}

/**
 * The one grid cell that the live value and its width reservation share.
 *
 * A count-up's string grows as it climbs — `0.0%` is three characters
 * narrower than the `82.3%` it settles on — so a span sized to the current
 * frame re-flows the text around it on almost every frame. With several
 * figures animating in one card the whole paragraph shimmers.
 *
 * So the widest frame the climb can produce is also rendered, hidden, into the
 * same cell: the cell is as wide as the wider of the two, and that reservation
 * is in place from the first frame. Both spans inherit the same face and size,
 * so it is exact — no `ch` arithmetic to keep in sync with the pill's
 * monospace face, and it still holds if the value is ever set in a
 * proportional font.
 *
 * The live value is right-aligned in the cell: digits then arrive on the left
 * and every character already on screen keeps its position, the least
 * movement a count-up can make. `prefix` and `suffix` sit *inside* both spans
 * so they ride along with the digits — a `$` parked at the cell's left edge
 * would sit a pill's width away from the number it belongs to until the last
 * frame.
 */
const RESERVED_CELL: CSSProperties = { gridArea: "1 / 1" };

interface CountUpNumberProps {
  value: number;
  delay?: number;
  format?: (value: number) => string;
  className?: string;
  prefix?: string;
  suffix?: string;
  /**
   * Fraction digits the value was written with, from a caller that still has
   * the source text (`authoredPlaces("45.60")` -> 2). Defaults to the target's
   * own precision, which has already lost any trailing zero.
   */
  fractionDigits?: number;
}

/**
 * Animates a number counting up from 0 to its target value
 * Supports comma formatting and custom formatting functions
 */
export function CountUpNumber({
  value,
  delay = 0,
  format,
  className = "",
  prefix = "",
  suffix = "",
  fractionDigits,
}: CountUpNumberProps) {
  const prefersReducedMotion = useReducedMotion();
  const [hasStarted, setHasStarted] = useState(false);

  /** Fraction digits the settled value carries, held on every frame. */
  const settledPlaces = useMemo(
    () => countUpPlaces(value, false, fractionDigits),
    [fractionDigits, value],
  );

  // First frame is a formatted zero ("0.0" for a 1dp target), not a bare "0",
  // so the fraction digits are in place before the spring produces anything.
  const [displayValue, setDisplayValue] = useState(() =>
    formatCountUpValue(0, format, settledPlaces),
  );

  const finalValue = useMemo(
    () => formatCountUpValue(value, format, settledPlaces),
    [format, settledPlaces, value],
  );

  // What the reservation holds: the settled value at the *widest* precision a
  // frame can use. Identical to finalValue for everything the narrative text
  // animates (targets of 10 and up); a target under 10 is smoothed to 1dp
  // while it climbs, so "8" reserves the "8.0" its own frames are as wide as.
  const reservedValue = useMemo(
    () =>
      formatCountUpValue(
        value,
        format,
        countUpPlaces(value, true, fractionDigits),
      ),
    [format, fractionDigits, value],
  );

  // Adjust spring parameters based on number size for better visibility
  const springConfig = useMemo(() => {
    if (value < 100) {
      // Slower animation for small numbers - lower stiffness
      return { stiffness: 40, damping: 18, mass: 1.0 };
    }
    if (value < 1000) {
      // Medium speed for medium numbers
      return { stiffness: 60, damping: 20, mass: 0.8 };
    }
    // Slightly faster for large numbers
    return { stiffness: 70, damping: 22, mass: 0.7 };
  }, [value]);

  // Create animated spring value with adjusted settings for smoothness
  const spring = useSpring(0, springConfig);

  // Transform spring value to match target with smart rounding
  const display = useTransform(spring, (latest) =>
    formatCountUpValue(
      quantizeCountUp(latest, value, fractionDigits),
      format,
      settledPlaces,
    ),
  );

  const hasStartedRef = useRef(hasStarted);
  hasStartedRef.current = hasStarted;

  // Subscribe to display changes
  useEffect(() => {
    const unsubscribe = display.on("change", (latest) => {
      setDisplayValue(latest);
    });
    return unsubscribe;
  }, [display]);

  useEffect(() => {
    // Start animation after delay
    const timer = setTimeout(() => {
      if (!hasStartedRef.current) {
        setHasStarted(true);
        spring.set(value);
      }
    }, delay * 1000);

    return () => clearTimeout(timer);
  }, [value, delay, spring]);

  // Reduced motion: skip the count-up spring and show the target value
  // directly. Nothing animates, so there is no width to reserve.
  if (prefersReducedMotion) {
    return (
      <motion.span className={className}>
        {prefix}
        {finalValue}
        {suffix}
      </motion.span>
    );
  }

  return (
    <motion.span className={className}>
      <span style={{ display: "inline-grid" }}>
        <span
          aria-hidden="true"
          style={{ ...RESERVED_CELL, visibility: "hidden" }}
        >
          {prefix}
          {reservedValue}
          {suffix}
        </span>
        <span style={{ ...RESERVED_CELL, justifySelf: "end" }}>
          {prefix}
          {displayValue}
          {suffix}
        </span>
      </span>
    </motion.span>
  );
}
